import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IoMdKey, IoMdRefresh, IoMdArrowBack, IoMdCheckmarkCircle, IoMdCloseCircle } from 'react-icons/io';
import { MdWifiOff } from 'react-icons/md';

const VPNModal = ({ onBypass, onBack }) => {
  const [showSteps, setShowSteps] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const longPressTimer = useRef(null);
  const BYPASS_PASSWORD = 'ecscc@go';
  const LONG_PRESS_DURATION = 1000;

  const handleMouseDown = () => {
    longPressTimer.current = setTimeout(() => {
      setShowPasswordInput(true);
    }, LONG_PRESS_DURATION);
  };

  const handleMouseUp = () => {
    clearTimeout(longPressTimer.current);
  };

  const handleMouseLeave = () => {
    clearTimeout(longPressTimer.current);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === BYPASS_PASSWORD) {
      onBypass();
      setShowPasswordInput(false);
      setPasswordError('');
      localStorage.setItem('bypassVpn','true')
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const handleTryAgain = () => {
    window.location.reload();
  };

  if (showPasswordInput) {
    return createPortal(
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center justify-center">
            <IoMdKey className="mr-2" /> Enter Bypass Key
          </h2>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              placeholder="Enter key"
              className="w-full p-3 mb-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError('');
              }}
            />
            {passwordError && <p className="text-red-500 text-sm mb-4">{passwordError}</p>}
            <div className="flex flex-col space-y-4">
              <button
                type="submit"
                className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-md transition duration-300 ease-in-out flex items-center justify-center"
              >
                <IoMdCheckmarkCircle className="mr-2" /> Submit Key
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordInput(false);
                  setPassword('');
                  setPasswordError('');
                }}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-md transition duration-300 ease-in-out flex items-center justify-center"
              >
                <IoMdCloseCircle className="mr-2" /> Cancel
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">VPN/Proxy Detected</h2>
        <p className="text-gray-700 mb-6">
          It looks like you're using a VPN or proxy service. For security and compliance reasons,
          access is restricted when a VPN/proxy is detected.
        </p>

        {showSteps ? (
          <div className="text-left bg-gray-100 p-4 rounded-md mb-6">
            <h3 className="font-semibold text-lg mb-2">How to Turn Off Your VPN:</h3>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li><b>Software VPN:</b> Locate your VPN application (e.g., ExpressVPN, NordVPN, ProtonVPN). Open it and find the "Disconnect" or "Turn Off" button.</li>
              <li><b>Browser Extension VPN:</b> If you are using a browser extension, click on its icon in your browser toolbar and look for an option to disable or turn off the VPN.</li>
              <li><b>Router VPN:</b> If your VPN is configured on your router, you may need to access your router's administration panel (usually via a web browser, e.g., 192.168.1.1) and disable the VPN client there.</li>
              <li><b>System Settings (Mobile/Desktop):</b> Check your device's network settings for VPN profiles and disconnect any active connections.</li>
            </ol>
            <button
              onClick={() => setShowSteps(false)}
              className="mt-4 text-blue-600 hover:underline"
            >
              Back to options
            </button>
          </div>
        ) : (
          <div className="flex flex-col space-y-4">
            <button
              onClick={() => setShowSteps(true)}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-md transition duration-300 ease-in-out flex items-center justify-center"
            >
              <MdWifiOff className="mr-2" /> Turn Off VPN
            </button>
            <button
              onClick={handleTryAgain}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleMouseDown}
              onTouchEnd={handleMouseUp}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-md transition duration-300 ease-in-out flex items-center justify-center"
            >
              <IoMdRefresh className="mr-2" /> Try Again
            </button>
            <button
              onClick={onBack}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-md transition duration-300 ease-in-out flex items-center justify-center"
            >
              <IoMdArrowBack className="mr-2" /> Go Back
            </button>
          </div>
        )}
        <p className="text-sm text-gray-500 mt-6">
          If you believe this is a mistake, please try turning off your VPN or using the bypass option.
        </p>
      </div>
    </div>,
    document.body
  );
};

export default VPNModal;
